import { useState } from 'react';
import { Wrench, CheckCircle } from 'lucide-react';
import { useCiclosManutencao } from '../hooks';
import type { MaintenanceType } from '../api';
import { MaintenanceStartModal } from './MaintenanceStartModal';
import { MaintenanceFinishModal } from './MaintenanceFinishModal';
import { formatarData, formatarMoeda } from '../utils/formatters';

const MAINTENANCE_TYPE_LABELS: Record<MaintenanceType, string> = {
  preventive: 'Preventiva',
  corrective: 'Corretiva',
  component_replacement: 'Troca de componentes',
  furniture_refurbishment: 'Reforma de mobiliário',
};

interface Props {
  assetId: string;
  canWrite: boolean;
  assetStatus: string;
}

export function ManutencaoTab({ assetId, canWrite, assetStatus }: Props) {
  const [startOpen, setStartOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const query = useCiclosManutencao(assetId);

  const hasOpenCycle = (query.data?.data ?? []).some((c) => c.finishedAt === null);
  const isRetired = assetStatus === 'retired';

  return (
    <div className="space-y-4">
      {canWrite && !isRetired && (
        <div className="flex flex-wrap items-center gap-2">
          {!hasOpenCycle && (
            <button
              onClick={() => setStartOpen(true)}
              className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-3 py-1.5 text-sm text-white hover:bg-purple-700"
            >
              <Wrench className="h-4 w-4" /> Enviar para manutenção
            </button>
          )}
          {hasOpenCycle && (
            <button
              onClick={() => setFinishOpen(true)}
              className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4" /> Registrar retorno
            </button>
          )}
        </div>
      )}

      {query.isLoading && (
        <p className="py-4 text-center text-sm text-gray-500">Carregando...</p>
      )}

      {query.data && query.data.data.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-500">Nenhum ciclo de manutenção registrado.</p>
      )}

      {query.data && query.data.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Início</th>
                <th className="px-3 py-2 text-left">Término</th>
                <th className="px-3 py-2 text-left">Fornecedor</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Custo</th>
                <th className="px-3 py-2 text-left">Descrição</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {query.data.data.map((c) => (
                <tr key={c.id} className={c.finishedAt === null ? 'bg-purple-50/40' : ''}>
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatarData(c.startedAt)}</td>
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                    {c.finishedAt ? formatarData(c.finishedAt) : <span className="text-purple-700 font-medium">Em andamento</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{c.provider ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{MAINTENANCE_TYPE_LABELS[c.maintenanceType]}</td>
                  <td className="px-3 py-2 text-gray-700">{c.cost ? formatarMoeda(c.cost) : '—'}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {c.startDescription}
                    {c.finishDescription && (
                      <>
                        <br /><span className="text-xs text-gray-500">Retorno: {c.finishDescription}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MaintenanceStartModal open={startOpen} onClose={() => setStartOpen(false)} assetId={assetId} />
      <MaintenanceFinishModal open={finishOpen} onClose={() => setFinishOpen(false)} assetId={assetId} />
    </div>
  );
}
